import { useCallback } from "react";

import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { showErrorToast } from "app/actions/toast_actions";

import { ONBOARDING_AGENT_DISPLAY_NAME } from "../../onboardingAgentConstants";
import { onboardingAgentTrackingService } from "../../onboardingAgentTrackingService";
import { ONBOARDING_AGENT_FEEDBACK_COPY_FAILED } from "../../texts";

import { useCopySuccessFeedback } from "./useCopySuccessFeedback";

type UseOnboardingAgentReplyCopyParams = {
    readonly plainText: string;
    readonly messageIndex: number;
    readonly threadId?: string;
    readonly translate: (key: string) => string;
};

export const useOnboardingAgentReplyCopy = ({
    plainText,
    messageIndex,
    threadId,
    translate,
}: UseOnboardingAgentReplyCopyParams) => {
    const { isCopied, notifyCopySuccess } = useCopySuccessFeedback();
    const dispatch = useDispatch();

    const handleClick = useCallback(async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(plainText);
            onboardingAgentTrackingService.trackCopy({ messageIndex, threadId });
            notifyCopySuccess();
        } catch (error) {
            onboardingAgentTrackingService.trackCopy({ messageIndex, threadId, failed: true });
            Sentry.errorTracker({
                e: error instanceof Error ? error : new Error(String(error)),
                message: `Failed to copy ${ONBOARDING_AGENT_DISPLAY_NAME} reply to clipboard`,
                level: "warning",
            });

            dispatch(showErrorToast(translate(ONBOARDING_AGENT_FEEDBACK_COPY_FAILED)));
        }
    }, [dispatch, messageIndex, notifyCopySuccess, plainText, threadId, translate]);

    const handleCopyButtonClick = (): void => {
        void handleClick();
    };

    return {
        isCopied,
        handleCopyButtonClick,
    };
};
